/**
 * Fantasy theme ambient background — amber embers drifting upward and pale
 * ash settling down, like the air near a hearth or a smouldering ruin.
 *
 * Plain Canvas 2D (no WebGL / no Three.js): keeps the bundle small, and a 2D
 * context is far less finicky than a lost/backgrounded WebGL context. Portaled
 * to <body> so its position:fixed anchors to the viewport rather than the
 * backdrop-blurred page wrapper (theme.background sets backdrop-filter, which
 * makes any fixed descendant resolve against that wrapper's box instead — the
 * same containing-block trap fixed in Modal.tsx). Purely decorative: never
 * intercepts pointer events, never mounts on the Glass theme, freezes for
 * prefers-reduced-motion, and pauses while the tab is hidden.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSafeTheme } from '@/shared/theme';

type Kind = 'ember' | 'ash';

interface Particle {
  x: number;
  y: number;
  size: number;
  speed: number; // px/sec along the vertical axis (embers rise, ash falls)
  drift: number; // horizontal sway amplitude
  swaySpeed: number;
  phase: number;
  life: number; // 0..1 progress through lifetime
  lifeSpeed: number; // 1/seconds
  kind: Kind;
}

const EMBER_COUNT = 46;
const ASH_COUNT = 34;

interface Palette {
  ember: [number, number, number]; // rgb
  ash: [number, number, number];
  emberComposite: GlobalCompositeOperation;
  emberAlpha: number;
  ashAlpha: number;
}

// On dark grounds bright amber with additive ('lighter') blending reads as a
// glowing spark; on light parchment additive washes out, so use a deeper,
// more saturated ember drawn normally, and a warmer, darker ash.
const PALETTES: Record<'light' | 'dark', Palette> = {
  dark: {
    ember: [255, 168, 71],
    ash: [206, 198, 182],
    emberComposite: 'lighter',
    emberAlpha: 0.9,
    ashAlpha: 0.28,
  },
  light: {
    ember: [200, 83, 12],
    ash: [120, 104, 84],
    emberComposite: 'source-over',
    emberAlpha: 0.7,
    ashAlpha: 0.3,
  },
};

function makeParticle(kind: Kind, width: number, height: number, seeded: boolean): Particle {
  const isEmber = kind === 'ember';
  return {
    x: Math.random() * width,
    // When seeding the initial field, scatter across the whole height so it
    // doesn't all stream in from one edge; afterwards, respawn just past the
    // spawn edge (bottom for embers, top for ash).
    y: seeded
      ? Math.random() * height
      : isEmber
        ? height + Math.random() * 40
        : -Math.random() * 40,
    size: isEmber ? 1 + Math.random() * 2 : 1.2 + Math.random() * 2.4,
    speed: isEmber ? 18 + Math.random() * 34 : 10 + Math.random() * 20,
    drift: isEmber ? 8 + Math.random() * 18 : 12 + Math.random() * 24,
    swaySpeed: 0.3 + Math.random() * 0.8,
    phase: Math.random() * Math.PI * 2,
    life: Math.random(),
    lifeSpeed: isEmber ? 0.05 + Math.random() * 0.08 : 0.03 + Math.random() * 0.05,
    kind,
  };
}

const FantasyEmberAsh: React.FC = () => {
  const { resolvedTheme } = useSafeTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const themeRef = useRef(resolvedTheme);
  themeRef.current = resolvedTheme;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const particles: Particle[] = [];
    for (let i = 0; i < EMBER_COUNT; i++) particles.push(makeParticle('ember', width, height, true));
    for (let i = 0; i < ASH_COUNT; i++) particles.push(makeParticle('ash', width, height, true));

    const drawFrame = (dt: number, time: number) => {
      ctx.clearRect(0, 0, width, height);
      const palette = PALETTES[themeRef.current === 'dark' ? 'dark' : 'light'];

      for (const p of particles) {
        const isEmber = p.kind === 'ember';
        p.y += (isEmber ? -p.speed : p.speed) * dt;
        p.x += Math.sin(time * p.swaySpeed + p.phase) * p.drift * dt;
        p.life += p.lifeSpeed * dt;

        // Respawn when off-screen or past end of life.
        if (p.life >= 1 || (isEmber ? p.y < -20 : p.y > height + 20)) {
          Object.assign(p, makeParticle(p.kind, width, height, false));
          continue;
        }

        // Fade in over the first 15%, hold, fade out over the last 45%.
        const fadeIn = Math.min(p.life / 0.15, 1);
        const fadeOut = p.life > 0.55 ? 1 - (p.life - 0.55) / 0.45 : 1;
        const envelope = fadeIn * fadeOut;

        if (isEmber) {
          // Gentle flicker on top of the lifetime envelope.
          const flicker = 0.65 + 0.35 * Math.sin(time * 6 + p.phase);
          const alpha = palette.emberAlpha * envelope * flicker;
          const [r, g, b] = palette.ember;
          ctx.globalCompositeOperation = palette.emberComposite;
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
          grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
          grad.addColorStop(0.4, `rgba(${r},${g},${b},${alpha * 0.5})`);
          grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const alpha = palette.ashAlpha * envelope;
          const [r, g, b] = palette.ash;
          ctx.globalCompositeOperation = 'source-over';
          ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalCompositeOperation = 'source-over';
    };

    let raf = 0;
    let last = performance.now();

    const loop = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05); // clamp big gaps (tab switches)
      last = now;
      drawFrame(dt, now / 1000);
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (!reduceMotion) start();
    };

    if (reduceMotion) {
      // Render a single static frame so the layer isn't blank, but don't animate.
      drawFrame(0, 0);
    } else {
      start();
    }

    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return createPortal(
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-0"
    />,
    document.body
  );
};

export default FantasyEmberAsh;
